import { useResizeSensor } from '@e2m-customer-portal/hooks';
import { Nullable } from '@e2m-customer-portal/shared';
import { CSSProperties, ReactElement, useCallback, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui-compat';

const SHADOW_SIZE = 24;

const useStyles = makeStyles()((theme) => ({
  shadowHelper: {
    transition: theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest / 2 }),
    opacity: 1,
    zIndex: 11,
    pointerEvents: 'none',
    position: 'absolute',
    width: SHADOW_SIZE,
    height: SHADOW_SIZE,
  },

  shadowTopFlat: {
    boxShadow: 'inset 0 7px 9px -7px rgba(0, 0, 0, .2)',
  },

  shadowRightFlat: {
    boxShadow: 'inset -7px 0 9px -7px rgba(0, 0, 0, .2)',
  },

  shadowBottomFlat: {
    boxShadow: 'inset 0 -7px 9px -7px rgba(0, 0, 0, .2)',
  },

  shadowLeftFlat: {
    boxShadow: 'inset 7px 0 9px -7px rgba(0, 0, 0, .2)',
  },

  shadowTopRound: {
    overflow: 'hidden',
    '&:after': {
      content: '""',
      display: 'block',
      boxShadow: '0 7px 9px -7px rgba(0, 0, 0, .2)',
      width: '100%',
      height: '100%',
      marginTop: -SHADOW_SIZE,
      borderBottomLeftRadius: '50% 100%',
      borderBottomRightRadius: '50% 100%',
    },
  },

  shadowRightRound: {
    overflow: 'hidden',
    '&:after': {
      content: '""',
      display: 'block',
      boxShadow: '-7px 0 9px -7px rgba(0, 0, 0, .2)',
      width: '100%',
      height: '100%',
      marginLeft: SHADOW_SIZE,
      borderTopLeftRadius: '100% 50%',
      borderBottomLeftRadius: '100% 50%',
    },
  },

  shadowBottomRound: {
    overflow: 'hidden',
    '&:after': {
      content: '""',
      display: 'block',
      boxShadow: '0 -7px 9px -7px rgba(0, 0, 0, .2)',
      width: '100%',
      height: '100%',
      marginTop: SHADOW_SIZE,
      borderTopLeftRadius: '50% 100%',
      borderTopRightRadius: '50% 100%',
    },
  },

  shadowLeftRound: {
    overflow: 'hidden',
    '&:after': {
      content: '""',
      display: 'block',
      boxShadow: '7px 0 9px -7px rgba(0, 0, 0, .2)',
      width: '100%',
      height: '100%',
      marginLeft: -SHADOW_SIZE,
      borderTopRightRadius: '100% 50%',
      borderBottomRightRadius: '100% 50%',
    },
  },
}));

export interface ScrollShadowsProps {
  /**
   * The variant of shadows to use. `flat` is the default, and will display uniform shadows across the whole
   * element. `round` shadows will round the shadows off towards the outer edges.
   */
  variant?: 'flat' | 'round';

  /**
   * The scrollable container to apply shadows to. Use the `ref` prop to retrieve the `HTMLElement`.
   */
  scrollContainer: Nullable<HTMLElement>;

  /**
   * If set, the shadow is offset this many pixels from the top. Useful for sticky headers.
   */
  offsetTop?: number;

  /**
   * If set, the shadow is offset this many pixels from the right. Useful for sticky headers.
   */
  offsetRight?: number;

  /**
   * If set, the shadow is offset this many pixels from the bottom. Useful for sticky headers.
   */
  offsetBottom?: number;

  /**
   * If set, the shadow is offset this many pixels from the left. Useful for sticky headers.
   */
  offsetLeft?: number;
}

function isSameProps(a: CSSProperties, b: CSSProperties): boolean {
  const keys = Object.keys(a);
  if (Object.keys(b).length !== keys.length) {
    return false;
  }

  for (const k of keys) {
    if (a[k as keyof CSSProperties] !== b[k as keyof CSSProperties]) {
      return false;
    }
  }

  return true;
}

export function ScrollShadows({
  variant = 'flat',
  scrollContainer,
  offsetTop = 0,
  offsetRight = 0,
  offsetBottom = 0,
  offsetLeft = 0,
}: Readonly<ScrollShadowsProps>): ReactElement<ScrollShadowsProps> {
  const { classes, cx } = useStyles();
  const [topStyle, setTopStyle] = useState<CSSProperties>({});
  const [rightStyle, setRightStyle] = useState<CSSProperties>({});
  const [bottomStyle, setBottomStyle] = useState<CSSProperties>({});
  const [leftStyle, setLeftStyle] = useState<CSSProperties>({});

  const refreshTop = useCallback(
    (container: HTMLElement) => {
      const nextTopStyle = {
        opacity: container.scrollTop > 0 ? 1 : 0,
        top: container.offsetTop + offsetTop,
        left: container.offsetLeft,
        width: container.clientWidth,
      };

      if (!isSameProps(topStyle, nextTopStyle)) {
        setTopStyle(nextTopStyle);
      }
    },
    [offsetTop, topStyle],
  );

  const refreshRight = useCallback(
    (container: HTMLElement) => {
      const nextRightStyle = {
        opacity: container.scrollLeft + container.clientWidth < container.scrollWidth ? 1 : 0,
        top: container.offsetTop,
        left: container.offsetLeft + container.clientWidth - SHADOW_SIZE - offsetRight,
        height: container.clientHeight,
      };

      if (!isSameProps(rightStyle, nextRightStyle)) {
        setRightStyle(nextRightStyle);
      }
    },
    [offsetRight, rightStyle],
  );

  const refreshBottom = useCallback(
    (container: HTMLElement) => {
      const nextBottomStyle = {
        opacity: container.scrollTop + container.clientHeight < container.scrollHeight ? 1 : 0,
        top: container.offsetTop + container.clientHeight - SHADOW_SIZE - offsetBottom,
        left: container.offsetLeft,
        width: container.clientWidth,
      };

      if (!isSameProps(bottomStyle, nextBottomStyle)) {
        setBottomStyle(nextBottomStyle);
      }
    },
    [offsetBottom, bottomStyle],
  );

  const refreshLeft = useCallback(
    (container: HTMLElement) => {
      const nextLeftStyle = {
        opacity: container.scrollLeft > 0 ? 1 : 0,
        top: container.offsetTop,
        left: container.offsetLeft + offsetLeft,
        height: container.clientHeight,
      };

      if (!isSameProps(leftStyle, nextLeftStyle)) {
        setLeftStyle(nextLeftStyle);
      }
    },
    [offsetLeft, leftStyle],
  );

  const handler = useCallback(() => {
    if (!scrollContainer) {
      return;
    }

    const parent = scrollContainer.parentElement;
    if (parent && parent.style.position !== 'relative') {
      parent.style.position = 'relative';
    }

    refreshTop(scrollContainer);
    refreshRight(scrollContainer);
    refreshBottom(scrollContainer);
    refreshLeft(scrollContainer);
  }, [scrollContainer, refreshTop, refreshRight, refreshBottom, refreshLeft]);

  useEffect(() => {
    if (!scrollContainer) return undefined;
    scrollContainer.addEventListener('scroll', handler);
    return () => scrollContainer.removeEventListener('scroll', handler);
  }, [handler, scrollContainer]);

  useResizeSensor(scrollContainer, handler, [scrollContainer, handler]);

  useEffect(handler, [handler]);

  return (
    <>
      <div
        className={cx(classes.shadowHelper, {
          [classes.shadowTopFlat]: variant === 'flat',
          [classes.shadowTopRound]: variant === 'round',
        })}
        style={topStyle}
      />
      <div
        className={cx(classes.shadowHelper, {
          [classes.shadowRightFlat]: variant === 'flat',
          [classes.shadowRightRound]: variant === 'round',
        })}
        style={rightStyle}
      />
      <div
        className={cx(classes.shadowHelper, {
          [classes.shadowBottomFlat]: variant === 'flat',
          [classes.shadowBottomRound]: variant === 'round',
        })}
        style={bottomStyle}
      />
      <div
        className={cx(classes.shadowHelper, {
          [classes.shadowLeftFlat]: variant === 'flat',
          [classes.shadowLeftRound]: variant === 'round',
        })}
        style={leftStyle}
      />
    </>
  );
}
